import * as React from 'react';
import {connect} from 'react-redux';
import {withStyles, WithStyles, Checkbox, CircularProgress} from '@material-ui/core';
import Button from '@components/common/button/Button';
import TranslateNew from '@translate/TranslateNew';
import { ChatStyle } from './Chat.style';
import ImageIcon from '@material-ui/icons/PanoramaOutlined';
import { IChat, IChatParticipant, ICreateChat, IChatAlteration, ChatType } from '@app-types/websocket/chat.types';
import { IFamilyMember } from '@app-types/websocket/family.types';
import * as ChatActions from '@services/chat/chat.actions';
import * as FileActions from '@services/file/file.actions';
import { displayErrorMessage } from '@services/user/user.actions';
import { FamilyMemberType } from '@services/family/family.constants';
import ImgAvatar from '@components/common/avatar/ImgAvatar';
import { IStuffBeingUploaded, IFinishedUpload, IUploadFile, IStuffToUpload } from '@app-types/websocket/file.types';
import { handleFileUpload, handleBlobUpload, handleImageUpload, mimeFromFilename } from '@helpers/uploadHelper';
import CordovaContext from 'src/cordovaContext';

const tn: TranslateNew = TranslateNew.getInstance();

interface IProps extends WithStyles<typeof ChatStyle> {
  open:boolean;
  userId:number;
  chat?:IChat;
  family:IFamilyMember[];
  isAuthenticated:boolean;
  close:() => void;
  uploadingFiles: IStuffBeingUploaded[];
  pendingUploads: IStuffToUpload[];
  uploadedFiles: IFinishedUpload[];
  uploadFile: (file: IUploadFile) => void;
  uploadedFileUsed: (fileId: number) => void;
  createChat:(data:ICreateChat) => void;
  renameChat:(data:IChatAlteration) => void;
  displayErrorMessage: (msg: string) => void;
}

interface IState {
  title:string;
  participants:number[];
  icon:string;
  titleTouched:boolean;
  internalUploadId:number|undefined;
}


class EditChat extends React.Component<IProps, IState> {

  private titleInputRef = React.createRef<HTMLInputElement>();

  constructor(props: IProps) {
    super(props);
    this.state = this.createState(props.userId, props.family, props.chat);
  }

  public componentDidMount() {
    this.focusTitle();
  }

  public componentDidUpdate(prevProps:IProps) {
    const prevChatId = prevProps.chat ? prevProps.chat.id : undefined;
    const chatId = this.props.chat ? this.props.chat.id : undefined;
    const chatChanged = prevChatId !== chatId;
    const dialogJustOpened = !prevProps.open && this.props.open;

    if (chatChanged || dialogJustOpened) {
      this.setState(this.createState(this.props.userId, this.props.family, this.props.chat));
    }
    if (dialogJustOpened) {
      this.focusTitle();
    }
  }

  public render() {

    const {classes, chat} = this.props;
    const family = Array.isArray(this.props.family) ? this.props.family : [];
    const {icon, title, internalUploadId} = this.state;
    
    const myRole = family && family.find((m) => m.id === this.props.userId);

    const uploadedIconFile = internalUploadId !== undefined
      ? this.props.uploadedFiles.find((f) => f.internalId === internalUploadId)
      : undefined;
    const uploadingIconFile = internalUploadId !== undefined
      ? this.props.uploadingFiles.find((f) => f.internalId === internalUploadId)
      : undefined;
    const pendingIconFile = internalUploadId !== undefined
      ? this.props.pendingUploads.find((f) => f.internalId === internalUploadId)
      : undefined;
    const iconUploadPending = internalUploadId !== undefined
      && uploadedIconFile === undefined
      && (uploadingIconFile !== undefined || pendingIconFile !== undefined);

    let iconElement:JSX.Element|undefined;

    // uploading
    if (uploadedIconFile) {
      iconElement = <img src={uploadedIconFile.permanentURL} className={classes.editIconImg} />
    } 
    if (uploadingIconFile && iconElement === undefined) {
      iconElement = <CircularProgress variant="determinate" value={uploadingIconFile.loaded} />
    }
    if (iconUploadPending && iconElement === undefined) {
      iconElement = <CircularProgress variant="indeterminate" />;
    }

    // not uploading
    if (icon.trim() === "" && iconElement === undefined) { // no icon

        iconElement = <label htmlFor="add-file-chatEdit">
          <input type="file"  multiple={false } style = {{opacity: 0, position: 'absolute'}}
            id={"add-file-chatEdit"} onChange={this.loadLocalFile} accept="capture=camera,image/*"/>
          <ImageIcon style={{fill:"#fff", fontSize:'36px'}}/>
        </label>
      
    } else if (iconElement === undefined ){ // we have an icon
      
        iconElement = <label htmlFor="add-file-chatEdit">
          <input type="file"  multiple={false } style = {{opacity: 0, position: 'absolute'}}
            id={"add-file-chatEdit"} onChange={this.loadLocalFile} accept="capture=camera,image/*"/>
            <img src={icon} className={classes.editIconImg} />
        </label>
      
    }

    return (
      <div className={classes.editChat}>
        <div className={classes.editIcon} style={icon.includes("/")?{overflow:'hidden'}:{}}>
          {iconElement}
        </div>
        <input
          id="chat-title"
          type="text"
          ref={this.titleInputRef}
          value={title}
          spellCheck={true}
          maxLength={25}
          onBlur={() => this.setState({titleTouched:true})}
          onChange={(event) => {this.setState({title:event.target.value})}}
          className={classes.greyInput}
          style={{flex:1, margin:'20px', marginBottom:0}}
          placeholder={tn.lang.chatSubject}/>
        <div>
        <div className={classes.error} style={(title.length === 0 && this.state.titleTouched)?{maxHeight:'16px'}:{}}>
          {tn.lang.createChatNoTopic}
        </div>
          {family.map((mem) => this.renderFamilyMember(mem, myRole ? myRole.role : FamilyMemberType.DELETED))}
        </div>
        <div className={classes.editActions}>
          <Button variant="outlined"  classes={{outlined:classes.buttonOutlinedLarge}} onClick={() => this.props.close()}>
            {tn.lang.cancel}
          </Button>
          <Button variant="contained" onClick={() => this.save()}
            disabled={!this.isValid() || iconUploadPending || !this.props.isAuthenticated} color="primary"
            classes={{contained:classes.buttonContainedLarge}}>
              {!this.props.isAuthenticated
                ? tn.lang.initializing
                : (iconUploadPending ? tn.lang.uploading : (chat?tn.lang.save:tn.lang.createChat))}
          </Button>
        </div>
      </div>
    );  
  }

  private focusTitle() {
    // only when creating a new chat; the Dialog steals focus on mount, so focus after it settles
    if (this.props.chat === undefined && this.props.open) {
      setTimeout(() => {
        if (this.titleInputRef.current) {
          this.titleInputRef.current.focus();
        }
      }, 100);
    }
  }

  private renderFamilyMember(mem:IFamilyMember, myRole:FamilyMemberType):JSX.Element|null {
    if (mem.role === FamilyMemberType.CHILD || mem.role === FamilyMemberType.PARENT || mem.role === FamilyMemberType.VIEWER) {
      return(
      <div key={""+mem.id} className={this.props.classes.editParticipant} onClick={() => this.clickParticipant(mem, myRole)}>
        <ImgAvatar imgName={mem.avatar} color={mem.color} style={{ marginRight: '3px' }}
            imgUrl={mem.imageAvatar ? mem.avatarURL : undefined} />
        <div className={this.props.classes.editParticipantName}>{mem.firstName}</div>
        <Checkbox color="primary" checked={this.state.participants.includes(mem.id)} readOnly={true} edge="start"
          disabled={(this.props.chat !== undefined && 
            ((myRole !== FamilyMemberType.PARENT && (this.props.chat.participants || {})[""+mem.id] !== undefined)
              || (mem.role === FamilyMemberType.PARENT && this.props.chat && (this.props.chat.participants || {})[""+mem.id] !== undefined)))
            || mem.id === this.props.userId} />
        </div>);
    } else {
      return null;
    }
  }

  private clickParticipant(mem:IFamilyMember, myRole:FamilyMemberType) {
    if (mem.id === this.props.userId) {return;}

    if (this.props.chat !== undefined) {
      const participants = this.props.chat.participants ? this.props.chat.participants : {};
      if (participants[""+mem.id] !== undefined){
        
        if (mem.role === FamilyMemberType.PARENT) {return;}
        if (myRole !== FamilyMemberType.PARENT) {return;}
      } 
    }

    
    if (this.state.participants.includes(mem.id)) {
      this.setState({participants:this.state.participants.filter((p) => p !== mem.id)});
    } else {
      const participants = this.state.participants.slice();
      participants.push(mem.id);
      this.setState({participants});
    }
  }

  private save() {
    if (this.props.chat && this.isValid()) {

      let icon:string|undefined = this.props.chat.icon;
      if (this.props.uploadedFiles && this.state.internalUploadId) {
        const file = this.props.uploadedFiles.find((f) => f.internalId === this.state.internalUploadId);
        if (file) {
          icon = file.permanentURL;
        }
      }
      let newParticipantList:number[]|undefined;
      const chatParticipantIds = Object.keys(this.props.chat.participants || {}).map(id => parseInt(id, 10));
      if (!areArraysEqual(chatParticipantIds, this.state.participants)) {
        newParticipantList = this.state.participants;
      }
      this.props.renameChat({
        id: this.props.chat.id,
        title:this.state.title,
        icon,
        participants:newParticipantList,
      })
      this.props.close();
    } else if (this.isValid()) {

      let icon:string|undefined = this.state.icon;
      if (this.props.uploadedFiles && this.state.internalUploadId) {
        const file = this.props.uploadedFiles.find((f) => f.internalId === this.state.internalUploadId);
        if (file) {
          icon = file.permanentURL;
        }
      } 

      this.props.createChat({
        chatType: ChatType.CHAT,
        participants: this.state.participants,
        title: this.state.title,
        icon,
        msg: "",
        referenceObject: undefined,
      });
      this.props.close();
    }
  }

  private isValid():boolean {
    if (this.state.title.trim().length > 0 && (this.state.participants.length > 1 || this.props.chat !== undefined)) {
      return true;
    }
    return false;
  }

  private createState(userId:number, family:IFamilyMember[] = [], chat?:IChat):IState {
    const participants:number[] = [];

    if (chat && chat.participants) {
      for (const participantIdString of Object.keys(chat.participants || {})) {
        const participantId:number = Number(participantIdString);
        if (participantId !== undefined && Number.isFinite(participantId) ) {
          participants.push(participantId);
        }
      }
    } else {
      participants.push(userId);
      for (const mem of family){
        if (mem.id !== userId && mem.role === FamilyMemberType.PARENT) {
          participants.push(mem.id);
        }
      }
    }

    return {
      title:chat && typeof chat.title === "string" ? chat.title:"",
      participants,
      icon:(chat && chat.icon)?chat.icon:"",
      titleTouched:false,
      internalUploadId:undefined,
    }
  }

  
  private loadLocalFile = async (event: React.ChangeEvent<HTMLInputElement>)  => {
    const input = event.currentTarget;

    try {
      const { files } = input;
      if (files && files.length > 0) {
        const f:File|null = files.item(0);
        if (f !== null) {
          const mime = (f.type || mimeFromFilename(f.name)).toLowerCase();
          const mimeIsMissing = !f.type || f.type.trim().length === 0;
          const isImage = mime.includes("image") || mimeIsMissing;

          if (isImage) {
            const data = await f.arrayBuffer();
            const imageMime = mime.includes("image") ? mime : "image/jpeg";
            const blob = new Blob([data], { type: imageMime });
            const previewUrl = URL.createObjectURL(blob);

            const fileName = chatIconFileName(imageMime, f.name);
            const fileUpload = handleImageUpload(blob, fileName, previewUrl, this.props.uploadFile);
            this.setState({internalUploadId:fileUpload.internalId});
          } else {
            const fileUpload = handleFileUpload(f,this.props.uploadFile);
            this.setState({internalUploadId:fileUpload.internalId});
          }
        }
      }
    } catch (error) {
      console.error("Failed to read selected chat icon file", error);
      this.props.displayErrorMessage("Could not read the selected image. Please try another image.");
    } finally {
      // allow selecting the same file again
      try {
        input.value = "";
      } catch (_) {
        // ignore
      }
    }
  }

  private imageLoaded(blob:Blob) {
    const fileUpload = handleBlobUpload(blob,"chatIcon.jpeg",this.props.uploadFile);
    this.setState({internalUploadId:fileUpload.internalId});
  }

  private imageFailed(error:string) {
    console.log(error);
  }
}

EditChat.contextType = CordovaContext;

function mapStateToProps(state: any) {
  const userId = state?.user?.session?.data?.id ?? 0;

    return {
    userId,
    isAuthenticated: !!state?.connection?.authenticated || !!state?.connection?.loadCompleted,
    family:state?.family?.family ? state.family.family : [],
    pendingUploads: state?.file?.dataToUpload ? state.file.dataToUpload : [],
    uploadingFiles: state?.file?.uploadSlots ? state.file.uploadSlots : [],
    uploadedFiles: state?.file?.uploadedFiles ? state.file.uploadedFiles : [],
  };
}

const mapDispatchToProps = {
  createChat: ChatActions.createChat,
  renameChat: ChatActions.renameChat,
  uploadedFileUsed: FileActions.uploadedFileUsed,
  uploadFile: FileActions.requestFileUpload,
  displayErrorMessage,
};

const areArraysEqual = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false;
  // Sort both arrays before comparing
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

function chatIconFileName(imageMime:string, originalName:string):string {
  const originalExtension = (originalName || "").split(".").pop()!.toLowerCase();
  const mimeSubtype = (imageMime.split("/")[1] || "").split(/[;+]/)[0].toLowerCase();
  const mimeExtension = mimeSubtype === "jpeg" ? "jpg" : mimeSubtype;
  const extension = /^[a-z0-9]{1,10}$/.test(mimeExtension)
    ? mimeExtension
    : (/^[a-z0-9]{1,10}$/.test(originalExtension) ? originalExtension : "jpg");
  return "chat-icon."+extension;
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(ChatStyle)(EditChat));



// WEBPACK FOOTER //
// ./src/components/chat/EditChat.tsx